import { useMemo, useState, type FormEvent } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useAuth, useCan } from "../auth/AuthProvider";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  LoadingBlock,
  PageHeader,
  RoleBadge,
  Select,
} from "../components/ui";
import { ConfirmDialog, Modal } from "../components/Modal";
import { CloseOrgCard } from "./CloseOrgCard";
import { CopyField } from "../components/CopyField";
import { ApiError } from "../lib/api";
import { relativeTime } from "../lib/format";
import { filterMembers, initials, sortMembers } from "../lib/team";
import {
  useInvitations,
  useInviteMember,
  useLeaveOrg,
  useMembers,
  useRemoveMember,
  useResendInvitation,
  useRevokeInvitation,
  useUpdateMember,
  useApps,
  useBilling,
  useDatabases,
} from "../lib/queries";
import type { Invitation, Member, MemberRole } from "../lib/types";

const ASSIGNABLE: MemberRole[] = ["viewer", "developer", "admin"];
const MIN_SEARCH = 6;

/* The three a member can be invited as, in the order they are offered. Each
   one's name and what it may do are copy, and live in `team.json`. */
const ROLE_OPTIONS = ["viewer", "developer", "admin"] as const satisfies readonly MemberRole[];

type ConfirmState =
  | { kind: "remove"; member: Member }
  | { kind: "promote"; member: Member }
  | { kind: "revoke"; invitation: Invitation }
  | { kind: "leave" };

/*
 * One neutral tint, not four coloured ones.
 *
 * This used to pick deterministically from mint, amber, purple and blue so the
 * same person always got the same colour. The problem was what those colours
 * already mean three rows away: mint is "live", amber is "heading for trouble",
 * blue is "you can click this". An avatar tinted amber beside a badge tinted
 * amber makes both of them decoration, and purple was not in the palette at all.
 *
 * The name is directly beside the initials in every place this renders, so the
 * tint was never what identified anyone.
 */
const INITIALS_STYLE = "bg-raised text-fg-muted";

export function TeamPage() {
  const { t } = useTranslation("team");
  /* Roles are named in the shared status namespace, because the badge on this
     page and the one in the API keys list have to agree. */
  const roleName = useTranslation("status").t as (key: string, options?: object) => string;
  const { currentOrg, user, reloadSession } = useAuth();
  const canManage = useCan("admin");
  const isOwner = useCan("owner");
  const orgId = currentOrg?.id;

  const { data: members, isPending } = useMembers(orgId);
  const { data: invitations } = useInvitations(orgId, canManage);
  const inviteMember = useInviteMember(orgId ?? "");
  const revokeInvitation = useRevokeInvitation(orgId ?? "");
  const resendInvitation = useResendInvitation(orgId ?? "");
  const updateMember = useUpdateMember(orgId ?? "");
  const removeMember = useRemoveMember(orgId ?? "");
  const leaveOrg = useLeaveOrg(orgId ?? "");
  // For the balance the closure dialog names. Read here rather than inside the
  // card so it is one request on a page that already makes several.
  const billing = useBilling(orgId);
  /*
   * Only for the totals the closure card names. Both are already cached by the
   * pages that list them, so on any normal navigation this costs nothing — and
   * a customer arriving straight here pays two cheap reads for a card that
   * would otherwise ask them to delete an unnamed amount of their work.
   */
  const apps = useApps(orgId, 1);
  const databases = useDatabases(orgId, 1);

  const [search, setSearch] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<MemberRole>("developer");
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [issued, setIssued] = useState<{ token: string; email: string; emailed: boolean } | null>(
    null,
  );
  const [resent, setResent] = useState<{
    invitationId: string;
    email: string;
    token: string;
    emailed: boolean;
  } | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  const sorted = useMemo(() => sortMembers(members ?? []), [members]);
  const visible = useMemo(() => filterMembers(sorted, search), [sorted, search]);
  const showSearch = Boolean(members && members.length >= MIN_SEARCH);

  async function invite(event: FormEvent) {
    event.preventDefault();
    setInviteError(null);
    setIssued(null);
    try {
      const invitation = await inviteMember.mutateAsync({ email: email.trim(), role });
      setIssued({
        token: invitation.token,
        email: email.trim(),
        emailed: invitation.emailed,
      });
      setEmail("");
    } catch (cause) {
      setInviteError(cause instanceof ApiError ? cause.message : t("modal.failed"));
    }
  }

  function closeInvite() {
    setInviteOpen(false);
    setInviteError(null);
    setIssued(null);
    setEmail("");
    setRole("developer");
  }

  function onRolePick(member: Member, nextRole: MemberRole) {
    if (nextRole === member.role) return;
    if (nextRole === "owner") {
      setConfirm({ kind: "promote", member });
      return;
    }
    updateMember.mutate({ userId: member.userId, role: nextRole });
  }

  async function onLeave() {
    await leaveOrg.mutateAsync();
    // The departed org is gone from the switcher; falling back to the next one
    // and showing its team beats sitting on a team page that no longer exists.
    await reloadSession();
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("title")}
        description={currentOrg ? t("description", { organization: currentOrg.name }) : undefined}
        action={
          canManage && (
            <Button icon="plus" onClick={() => setInviteOpen(true)}>
              {t("invite")}
            </Button>
          )
        }
      />

      {/* Invite-flows share one success pattern: the token is shown once, because
          a failed send must not strand the invitation. */}
      <Modal
        open={inviteOpen}
        onClose={closeInvite}
        title={issued ? t("modal.titleIssued") : t("modal.title")}
        description={issued ? undefined : t("modal.description")}
        footer={
          issued ? (
            <Button variant="secondary" onClick={closeInvite}>
              {t("modal.done")}
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={closeInvite}>
                {t("modal.cancel")}
              </Button>
              <Button type="submit" form="inviteForm" loading={inviteMember.isPending}>
                {t("modal.send")}
              </Button>
            </>
          )
        }
      >
        {issued ? (
          <div className="space-y-3">
            <Alert tone={issued.emailed ? "success" : "warning"}>
              <Trans
                ns="team"
                i18nKey={issued.emailed ? "modal.sent" : "modal.notSent"}
                values={{ email: issued.email }}
                components={{ who: <span className="font-medium" /> }}
              />
            </Alert>
            <CopyField value={issued.token} label={t("modal.token")} />
          </div>
        ) : (
          <form id="inviteForm" onSubmit={invite} className="space-y-5">
            {inviteError && <Alert>{inviteError}</Alert>}

            <Field label={t("modal.email")} htmlFor="inviteEmail" hint={t("modal.emailHint")}>
              <Input
                id="inviteEmail"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>

            <fieldset>
              <legend className="mb-2 block text-sm font-medium text-fg">{t("modal.role")}</legend>
              <div className="space-y-2">
                {ROLE_OPTIONS.map((option) => (
                  <label
                    key={option}
                    className="flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors"
                  >
                    <input
                      type="radio"
                      name="inviteRole"
                      value={option}
                      checked={role === option}
                      onChange={() => setRole(option)}
                      className="mt-0.5 accent-brand-600"
                    />
                    <span>
                      <span className="block text-sm font-medium text-fg">
                        {t(`roles.${option}.label`)}
                      </span>
                      <span className="block text-xs text-fg-subtle">
                        {t(`roles.${option}.description`)}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          </form>
        )}
      </Modal>

      <Card>
        <CardHeader
          title={t("members.title")}
          action={
            members && (
              <p className="text-sm text-fg-subtle">
                {t("members.count", { count: members.length })}
              </p>
            )
          }
        />
        {isPending ? (
          <LoadingBlock />
        ) : (
          <>
            {showSearch && (
              <div className="border-b border-line px-5 py-3">
                <Input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={t("members.search")}
                  aria-label={t("members.searchLabel")}
                />
              </div>
            )}

            {visible.length === 0 ? (
              <EmptyState
                title={search ? t("members.noMatch") : t("members.empty")}
                description={
                  search
                    ? t("members.noMatchDescription", { query: search.trim() })
                    : t("members.emptyDescription")
                }
              />
            ) : (
              <ul className="divide-y divide-line">
                {visible.map((member) => {
                  const isSelf = member.userId === user?.id;
                  const roleError =
                    updateMember.isError && updateMember.variables?.userId === member.userId
                      ? updateMember.error.message
                      : null;
                  return (
                    <li key={member.userId} className="px-5 py-4">
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="flex min-w-0 flex-1 items-center gap-3 max-sm:basis-full">
                          <span
                            aria-hidden="true"
                            className={`grid size-8 shrink-0 place-items-center rounded-md border border-line text-[11px] font-medium ${INITIALS_STYLE}`}
                          >
                            {initials(member.name)}
                          </span>

                          <div className="min-w-0 flex-1">
                            <p className="truncate font-medium text-fg">
                              {member.name}
                              {isSelf && <span className="text-fg-subtle"> {t("members.you")}</span>}
                            </p>
                            <p className="truncate text-sm text-fg-subtle">
                              {t("members.joined", {
                                email: member.email,
                                when: relativeTime(member.joinedAt),
                              })}
                            </p>
                          </div>
                        </div>

                        <div className="max-sm:ml-auto">
                          {canManage && !isSelf && member.role !== "owner" ? (
                            <Select
                              aria-label={t("members.roleFor", { name: member.name })}
                              value={member.role}
                              className="w-auto"
                              onChange={(event) =>
                                onRolePick(member, event.target.value as MemberRole)
                              }
                            >
                              {ASSIGNABLE.map((value) => (
                                <option key={value} value={value}>
                                  {roleName(value)}
                                </option>
                              ))}
                              {isOwner && <option value="owner">{roleName("owner")}</option>}
                            </Select>
                          ) : (
                            <RoleBadge role={member.role} />
                          )}
                        </div>

                        {canManage && !isSelf && (
                          <Button
                            variant="ghost"
                            size="sm"
                            loading={
                              removeMember.isPending && removeMember.variables === member.userId
                            }
                            onClick={() => setConfirm({ kind: "remove", member })}
                          >
                            {t("members.remove")}
                          </Button>
                        )}
                      </div>

                      {roleError && (
                        <p className="mt-2 text-sm text-danger">{roleError}</p>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {!isOwner && (
              <div className="flex items-center justify-between gap-3 border-t border-line px-5 py-4">
                <p className="text-sm text-fg-subtle">{t("members.leaveWarning")}</p>
                <Button variant="ghost" size="sm" onClick={() => setConfirm({ kind: "leave" })}>
                  {t("members.leave")}
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      {canManage && (
        <Card>
          <CardHeader
            title={t("invitations.title")}
            description={
              invitations && invitations.length > 0 ? t("invitations.description") : undefined
            }
          />
          {invitations && invitations.length > 0 ? (
            <>
              {resent && (
                <div className="space-y-3 border-b border-line px-5 py-4">
                  <Alert tone={resent.emailed ? "success" : "warning"}>
                    {resent.emailed
                      ? t("invitations.resent", { email: resent.email })
                      : t("invitations.resentNotEmailed", { email: resent.email })}
                  </Alert>
                  <CopyField value={resent.token} label={t("modal.token")} />
                </div>
              )}
              <ul className="divide-y divide-line">
                {invitations.map((invitation) => {
                  const expired = new Date(invitation.expiresAt).getTime() < Date.now();
                  return (
                    <li key={invitation.id} className="flex flex-wrap items-center gap-3 px-5 py-4">
                      <div className="min-w-0 flex-1 max-sm:basis-full">
                        <p className="truncate text-sm font-medium">{invitation.email}</p>
                        <p className="text-xs text-fg-subtle">
                          {expired ? (
                            <span className="text-danger">{t("invitations.expired")}</span>
                          ) : (
                            t("invitations.expires", { when: relativeTime(invitation.expiresAt) })
                          )}
                        </p>
                      </div>
                      <div className="ml-auto flex flex-wrap items-center gap-2">
                        <RoleBadge role={invitation.role} />
                        <Button
                          variant="secondary"
                          size="sm"
                          loading={
                            resendInvitation.isPending &&
                            resendInvitation.variables === invitation.id
                          }
                          onClick={() =>
                            void resendInvitation
                              .mutateAsync(invitation.id)
                              .then((result) =>
                                setResent({
                                  invitationId: invitation.id,
                                  email: invitation.email,
                                  token: result.token,
                                  emailed: result.emailed,
                                }),
                              )
                          }
                        >
                          {t("invitations.resend")}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          loading={
                            revokeInvitation.isPending && revokeInvitation.variables === invitation.id
                          }
                          onClick={() => setConfirm({ kind: "revoke", invitation })}
                        >
                          {t("invitations.revoke")}
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : (
            <EmptyState
              title={t("invitations.empty")}
              description={t("invitations.emptyDescription")}
            />
          )}
        </Card>
      )}

      {/* -----------------------------------------------------------------
        Confirmations. One dialog, four asks — the state picks the wording.
      */}
      {confirm?.kind === "remove" && (
        <ConfirmDialog
          open={true}
          onClose={() => setConfirm(null)}
          title={t("confirm.remove.title")}
          description={
            <Trans
              ns="team"
              i18nKey="confirm.remove.description"
              values={{ name: confirm.member.name, email: confirm.member.email }}
              components={{ who: <span className="font-medium text-fg" /> }}
            />
          }
          confirmLabel={t("confirm.remove.action")}
          loading={removeMember.isPending}
          onConfirm={() => {
            removeMember.mutate(confirm.member.userId, {
              onSuccess: () => setConfirm(null),
            });
          }}
        />
      )}

      {confirm?.kind === "promote" && (
        <ConfirmDialog
          open={true}
          onClose={() => setConfirm(null)}
          title={t("confirm.promote.title")}
          description={
            <Trans
              ns="team"
              i18nKey="confirm.promote.description"
              values={{ name: confirm.member.name }}
              components={{ who: <span className="font-medium text-fg" /> }}
            />
          }
          confirmLabel={t("confirm.promote.action")}
          tone="primary"
          loading={updateMember.isPending}
          onConfirm={() => {
            updateMember.mutate(
              { userId: confirm.member.userId, role: "owner" },
              { onSuccess: () => setConfirm(null) },
            );
          }}
        />
      )}

      {confirm?.kind === "revoke" && (
        <ConfirmDialog
          open={true}
          onClose={() => setConfirm(null)}
          title={t("confirm.revoke.title")}
          description={
            <Trans
              ns="team"
              i18nKey="confirm.revoke.description"
              values={{ email: confirm.invitation.email }}
              components={{ who: <span className="font-medium text-fg" /> }}
            />
          }
          confirmLabel={t("confirm.revoke.action")}
          loading={revokeInvitation.isPending}
          onConfirm={() => {
            revokeInvitation.mutate(confirm.invitation.id, {
              onSuccess: () => setConfirm(null),
            });
          }}
        />
      )}

      {confirm?.kind === "leave" && (
        <ConfirmDialog
          open={true}
          onClose={() => setConfirm(null)}
          title={t("confirm.leave.title")}
          description={
            <Trans
              ns="team"
              i18nKey="confirm.leave.description"
              values={{
                organization: currentOrg?.name ?? t("confirm.leave.thisOrganization"),
              }}
              /* Bold only when it is a name. Emphasising the words "this
                 organization" would be emphasising a pronoun. */
              components={{
                who: currentOrg?.name ? <span className="font-medium text-fg" /> : <span />,
              }}
            />
          }
          confirmLabel={t("confirm.leave.action")}
          loading={leaveOrg.isPending}
          onConfirm={() => {
            void onLeave().then(() => setConfirm(null));
          }}
        />
      )}

      {/*
        Last on the page, and the only thing here that deletes anything. §9 of
        the Terms points customers at "the dashboard" to close an account, and
        the Team page is where an organization is administered — so it is where
        the control to end one belongs.
      */}
      {currentOrg && (
        <CloseOrgCard
          org={currentOrg}
          balanceMicros={billing.data ? billing.data.balanceMicros : null}
          currency={billing.data?.currency ?? "USD"}
          isOwner={isOwner}
          appCount={apps.data ? apps.data.total : null}
          databaseCount={databases.data ? databases.data.total : null}
          onChanged={reloadSession}
        />
      )}
    </div>
  );
}
